import React, { useEffect, useState, useRef } from "react";
import {
  Send, Trash2, Bot, User, Loader2, FolderKanban
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  sendChatMessage, getChatHistory, clearChatHistory, getProjects
} from "../lib/api";

export default function ChatAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("general");
  const [loadingHistory, setLoadingHistory] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    getProjects({}).then((res) => setProjects(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    loadHistory();
  }, [selectedProject]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const pid = selectedProject === "general" ? null : selectedProject;
      const res = await getChatHistory(pid);
      setMessages(res.data);
    } catch {
      // Silent fail
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);

    // Optimistic UI
    const tempUserMsg = { role: "user", content: text, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, tempUserMsg]);

    try {
      const pid = selectedProject === "general" ? null : selectedProject;
      const res = await sendChatMessage({ message: text, project_id: pid });
      setMessages((prev) => {
        const filtered = prev.filter((m) => m !== tempUserMsg);
        return [...filtered, res.data.user_message, res.data.assistant_message];
      });
    } catch {
      toast.error("Failed to send message");
      setMessages((prev) => prev.filter((m) => m !== tempUserMsg));
    } finally {
      setSending(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Clear all chat history for this context?")) return;
    try {
      const pid = selectedProject === "general" ? null : selectedProject;
      await clearChatHistory(pid);
      setMessages([]);
      toast.success("Chat history cleared");
    } catch {
      toast.error("Failed to clear history");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="space-y-4" data-testid="chat-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#003057]" data-testid="chat-title">
            AI Assistant
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Discuss test results, get analysis insights, and refine reports
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-56 rounded-sm text-xs" data-testid="chat-project-select">
              <FolderKanban className="w-3.5 h-3.5 mr-1 text-slate-400" />
              <SelectValue placeholder="Select context" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="general">General (No Project)</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClear}
            className="rounded-sm border-slate-300 text-slate-500"
            data-testid="clear-chat-btn"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Chat area */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-sm flex flex-col" style={{ height: "calc(100vh - 220px)" }}>
        {/* Messages */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full" ref={scrollRef}>
            <div className="p-4 space-y-4" data-testid="chat-messages">
              {messages.length === 0 && !loadingHistory && (
                <div className="text-center py-16" data-testid="chat-empty">
                  <Bot className="w-12 h-12 mx-auto text-slate-300 mb-4" strokeWidth={1.5} />
                  <p className="text-sm font-medium text-slate-500">
                    Automotive Test Engineering Assistant
                  </p>
                  <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                    Ask about test results, request analysis summaries, compare test runs,
                    or get ASPICE/ISO 26262 compliance insights.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 mt-4">
                    {[
                      "Summarize the latest test results",
                      "What anomalies were detected?",
                      "Explain ASIL classification",
                      "Compare pass rates across versions",
                    ].map((suggestion, i) => (
                      <button
                        key={i}
                        onClick={() => setInput(suggestion)}
                        data-testid={`chat-suggestion-${i}`}
                        className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-full transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  data-testid={`chat-message-${i}`}
                >
                  {msg.role === "assistant" && (
                    <div className="w-7 h-7 rounded-sm bg-[#003057] flex items-center justify-center shrink-0 mt-0.5">
                      <Bot className="w-4 h-4 text-white" strokeWidth={1.5} />
                    </div>
                  )}
                  <div
                    className={`max-w-[75%] px-4 py-3 text-sm leading-relaxed ${
                      msg.role === "user" ? "chat-user" : "chat-assistant"
                    }`}
                  >
                    {msg.role === "assistant" ? (
                      <div className="prose prose-sm prose-slate max-w-none">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <p>{msg.content}</p>
                    )}
                  </div>
                  {msg.role === "user" && (
                    <div className="w-7 h-7 rounded-sm bg-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                      <User className="w-4 h-4 text-slate-600" strokeWidth={1.5} />
                    </div>
                  )}
                </div>
              ))}

              {sending && (
                <div className="flex gap-3" data-testid="chat-loading">
                  <div className="w-7 h-7 rounded-sm bg-[#003057] flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-white" strokeWidth={1.5} />
                  </div>
                  <div className="chat-assistant px-4 py-3">
                    <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Input */}
        <div className="border-t border-slate-200 p-4" data-testid="chat-input-area">
          <div className="flex gap-2">
            <Textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about test results, analysis, or compliance..."
              className="rounded-sm resize-none min-h-[44px] max-h-[120px]"
              rows={1}
            />
            <Button
              data-testid="chat-send-btn"
              onClick={handleSend}
              disabled={!input.trim() || sending}
              className="bg-[#003057] hover:bg-[#002545] text-white rounded-sm h-[44px] w-[44px] p-0 shrink-0"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5">
            Press Enter to send. Shift+Enter for new line. AI responses reference your uploaded test data.
          </p>
        </div>
      </Card>
    </div>
  );
}
